import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { Send, AlertCircle, Smile, Image, Lock, Film, Camera } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useSocket } from '../../contexts/SocketContext';
import { useMessageStore, type MessagingRestriction } from '../../store/messageStore';
import { socketService } from '../../services/socketService';
import { MessageService } from '../../services/messageService';
import { toast } from '../ui/Toaster';
import EmojiPicker from '../chat/EmojiPicker';
import GifPicker, { GifData } from '../chat/GifPicker';
import UpgradeModal from '../modals/UpgradeModal';
import { convertLastWordToEmoji } from '../../utils/simpleTextToEmoji';
import { isMobile } from '../../utils/mobileUtils';
import { getRecommendedInputPadding, onViewportChange } from '../../utils/safeAreaUtils';

interface DirectMessageInputProps {
  receiverId: string;
  disabled?: boolean;
  placeholder?: string;
  messagingRestriction?: MessagingRestriction | null;
}

const DirectMessageInput: React.FC<DirectMessageInputProps> = ({ 
  receiverId, 
  disabled = false,
  placeholder = 'Typ een bericht...',
  messagingRestriction = null
}) => {
  const [content, setContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitTimeLeft, setRateLimitTimeLeft] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [dynamicPadding, setDynamicPadding] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  
  // Client-side rate limiting tracking
  const messageTimestamps = useRef<number[]>([]);
  const MAX_MESSAGES = 30; // Higher limit for direct messages
  const TIME_WINDOW = 60000; // 60 seconds in milliseconds
  
  const { user, updateUser } = useAuthStore();
  const { isConnected } = useSocket();
  const { sendMessage: sendMessageViaStore } = useMessageStore();

  // Check if user has premium chat enhancements
  const hasPremiumChat = user?.chatEnhancements || false;

  // Auto-focus on component mount (desktop only)
  useEffect(() => {
    if (!isMobile()) {
      inputRef.current?.focus();
    }
  }, []);

  // Dynamic safe area detection for mobile devices
  useEffect(() => {
    if (!isMobile()) return;

    // Calculate initial padding
    const initialPadding = getRecommendedInputPadding();
    setDynamicPadding(initialPadding);

    // Listen for viewport changes (dynamic navigation bars and keyboard)
    const cleanup = onViewportChange(() => {
      const newPadding = getRecommendedInputPadding();
      setDynamicPadding(newPadding);
    });

    return cleanup;
  }, []);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  // Check if user is sending messages too fast (client-side rate limiting)
  const isClientRateLimited = () => {
    const now = Date.now();
    
    // Remove timestamps older than the time window
    messageTimestamps.current = messageTimestamps.current.filter(
      timestamp => now - timestamp < TIME_WINDOW
    );
    
    // Check if user has reached the limit
    return messageTimestamps.current.length >= MAX_MESSAGES;
  };

  // Add timestamp when message is sent
  const addMessageTimestamp = () => {
    messageTimestamps.current.push(Date.now());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendMessageContent();
  };

  const handleEmojiSelect = (emoji: string) => {
    const newContent = content + emoji;
    setContent(newContent);
    setShowEmojiPicker(false);
    
    // Focus back to input and set cursor to end (desktop only)
    if (!isMobile()) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newContent.length, newContent.length);
        }
      }, 50);
    }
  };

  const handleGifSelect = (gif: GifData) => {
    // Send GIF message immediately
    sendMessageContent({
      messageType: 'GIF',
      gifUrl: gif.url,
      gifId: gif.id,
    });
    setShowGifPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
    setShowGifPicker(false); // Close GIF picker if open
  };

  // The media button opens a small menu (GIF zoeken / Foto uploaden) for
  // premium users; without premium it opens the beer-donation upgrade flow.
  const toggleAttachMenu = () => {
    if (!hasPremiumChat) {
      setShowEmojiPicker(false);
      setShowGifPicker(false);
      setShowUpgradeModal(true);
      return;
    }
    setShowAttachMenu(!showAttachMenu);
    setShowEmojiPicker(false);
    setShowGifPicker(false);
  };

  // GIF tab inside the emoji picker: straight to the GIF picker
  const handleGifTabClick = () => {
    setShowEmojiPicker(false);
    if (hasPremiumChat) {
      setShowGifPicker(true);
    } else {
      setShowUpgradeModal(true);
    }
  };

  // Photo upload: validate client-side, upload via REST, then send the
  // returned URL as an IMAGE message
  const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // matches backend MAX_FILE_SIZE
  const handleImageFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Allow re-selecting the same file later
    if (!file) return;

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Fout', 'Alleen JPG- of PNG-foto\'s zijn toegestaan');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Fout', 'Foto te groot (max 2MB)');
      return;
    }

    try {
      setIsUploadingImage(true);
      const url = await MessageService.uploadChatImage(file);
      await sendMessageContent({ messageType: 'IMAGE', gifUrl: url });
    } catch (error: any) {
      console.error('Failed to upload chat image:', error);
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        toast.error('Fout', 'Foto uploaden mislukt');
      }
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessageContent();
    } else if (e.key === ' ') {
      // Convert text to emoji on spacebar press
      const converted = convertLastWordToEmoji(content);
      if (converted !== content) {
        setContent(converted + ' ');
        e.preventDefault(); // Prevent double space
      }
    } else if (e.key === 'Escape') {
      if (showEmojiPicker || showGifPicker || showAttachMenu) {
        // Close pickers first
        setShowEmojiPicker(false);
        setShowGifPicker(false);
        setShowAttachMenu(false);
      } else if (content.trim()) {
        // If there's content, clear it
        setContent('');
      } else {
        // If no content, blur the input
        inputRef.current?.blur();
      }
    }
  };

  const handleRateLimit = (timeLeft: number) => {
    setIsRateLimited(true);
    setRateLimitTimeLeft(timeLeft);
    
    // Clear any existing countdown
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    
    // Start countdown timer
    countdownRef.current = setInterval(() => {
      setRateLimitTimeLeft(prev => {
        if (prev <= 1) {
          setIsRateLimited(false);
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendMessageContent = async (messageData?: {
    messageType: 'TEXT' | 'EMOJI' | 'GIF' | 'IMAGE';
    gifUrl?: string;
    gifId?: string;
  }) => {
    const trimmed = content.trim();
    // GIF and IMAGE messages carry their content in gifUrl; text is an
    // optional caption for both
    const isMediaMessage = messageData?.messageType === 'GIF' || messageData?.messageType === 'IMAGE';

    // For media messages, content can be empty (no caption required)
    if (!isMediaMessage && (!trimmed || isSending || !isConnected || !user)) {
      return;
    }

    // For text/emoji messages, validate content
    if (!isMediaMessage && trimmed.length > 1000) {
      toast.error('Fout', 'Bericht te lang (max 1000 tekens)');
      return;
    }

    // For media captions, validate length
    if (isMediaMessage && trimmed.length > 500) {
      toast.error('Fout', 'Caption te lang (max 500 tekens)');
      return;
    }

    if (!isConnected || !user) {
      toast.error('Fout', 'Niet verbonden met chat');
      return;
    }

    // Check client-side rate limiting BEFORE doing anything
    if (isClientRateLimited()) {
      handleRateLimit(60);
      return;
    }

    // Track this message attempt for client-side rate limiting
    addMessageTimestamp();

    setContent('');
    
    // Close any open pickers
    setShowEmojiPicker(false);
    setShowGifPicker(false);
    
    // Maintain focus after sending message (desktop only)
    if (!isMobile()) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }

    try {
      setIsSending(true);
      
      // Check socket connection first
      if (!socketService.isConnected()) {
        toast.error('Fout', 'Niet verbonden met chat server');
        return;
      }
      
      // Use the store for all message types (store now uses socket service internally)
      if (isMediaMessage) {
        // For GIF/IMAGE messages, pass the media data to the store
        await sendMessageViaStore(receiverId, trimmed || '', messageData!.messageType, messageData!.gifUrl, messageData!.gifId);
      } else {
        // For text/emoji messages
        await sendMessageViaStore(receiverId, trimmed, messageData?.messageType || 'TEXT');
      }

      toast.success('Bericht verzonden');
    } catch (error: any) {
      console.error('Failed to send direct message:', error);
      
      if (error.message?.includes('rate limit')) {
        const timeMatch = error.message?.match(/(\d+) seconds?/);
        const timeLeft = timeMatch ? parseInt(timeMatch[1]) : 60;
        handleRateLimit(timeLeft);
      } else {
        toast.error('Fout', error.message || 'Kon bericht niet verzenden');
      }
    } finally {
      setIsSending(false);
    }
  };

  // Check if messaging is restricted
  const isMessagingRestricted = messagingRestriction && !messagingRestriction.canMessage;
  
  const isDisabled = Boolean(disabled || !isConnected || isSending || isRateLimited || isMessagingRestricted);
  
  const placeholderText = !isConnected 
    ? 'Niet verbonden met chat...' 
    : isRateLimited 
    ? `Te snel - wacht ${rateLimitTimeLeft}s...`
    : isMessagingRestricted
    ? (messagingRestriction?.reason === 'GROUPS_REQUIRED' 
        ? 'Je moet eerst lid worden van een groep met deze gebruiker...' 
        : messagingRestriction?.message || 'Kan geen berichten sturen...')
    : placeholder;

  return (
    <div
      className="border-t border-white/[.08] p-4 bg-surface relative"
      style={isMobile() ? { paddingBottom: `${dynamicPadding}px` } : {}}
    >
      {/* Emoji Picker */}
      {showEmojiPicker && (
        <EmojiPicker
          onEmojiSelect={handleEmojiSelect}
          onClose={() => setShowEmojiPicker(false)}
          onGifClick={handleGifTabClick}
        />
      )}

      {/* GIF Picker */}
      {showGifPicker && (
        <GifPicker
          onGifSelect={handleGifSelect}
          onClose={() => setShowGifPicker(false)}
        />
      )}

      {/* Attach menu: GIF zoeken / Foto uploaden */}
      {showAttachMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowAttachMenu(false)} />
          <div className="absolute bottom-full left-4 mb-2 z-50 w-56 rounded-card border border-white/[.08] bg-surface shadow-panel overflow-hidden">
            <button
              type="button"
              onClick={() => { setShowAttachMenu(false); setShowGifPicker(true); }}
              className="flex w-full items-center gap-3 px-4 py-3 text-sm font-medium text-fg-soft hover:bg-white/[.06] transition-colors"
            >
              <Film className="h-5 w-5 text-neon-cyan" />
              GIF zoeken
            </button>
            <button
              type="button"
              onClick={() => { setShowAttachMenu(false); fileInputRef.current?.click(); }}
              className="flex w-full items-center gap-3 px-4 py-3 text-sm font-medium text-fg-soft hover:bg-white/[.06] transition-colors border-t border-white/[.06]"
            >
              <Camera className="h-5 w-5 text-neon-pink" />
              Foto uploaden
            </button>
          </div>
        </>
      )}

      {/* Hidden file input for photo uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={handleImageFileSelected}
      />
      
      <form onSubmit={handleSubmit} className="flex space-x-2">
        {/* Input controls */}
        <div className="flex space-x-1">
          {/* Emoji button - always visible */}
          <button
            type="button"
            onClick={toggleEmojiPicker}
            className={`
              p-2 rounded-xl transition-colors flex items-center justify-center
              ${showEmojiPicker
                ? 'bg-neon-gradient-soft border border-neon-pink/[.32] text-neon-cyan'
                : 'text-fg-faint hover:text-neon-cyan hover:bg-white/[.06]'
              }
              ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            disabled={isDisabled}
            title="Emoji toevoegen"
          >
            <Smile className="h-5 w-5" />
          </button>

          {/* Media button - always visible; premium gets a GIF/photo menu,
              without premium it opens the upgrade flow */}
          <button
            type="button"
            onClick={toggleAttachMenu}
            className={`
              p-2 rounded-xl transition-colors flex items-center justify-center
              ${showGifPicker || showAttachMenu
                ? 'bg-neon-gradient-soft border border-neon-pink/[.32] text-neon-cyan'
                : 'text-fg-faint hover:text-neon-cyan hover:bg-white/[.06]'
              }
              ${isDisabled || isUploadingImage ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            disabled={isDisabled || isUploadingImage}
            title={hasPremiumChat ? 'GIF of foto sturen' : 'GIFs en foto\'s vrijspelen (steun ons met een biertje)'}
          >
            {isUploadingImage ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Image className="h-5 w-5" />
            )}
          </button>
        </div>
        
        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholderText}
            disabled={isDisabled}
            rows={1}
            className={`
              w-full px-3 py-2 border border-neon-pink/30 rounded-item text-sm text-fg
              placeholder-fg-faint focus:outline-none focus:ring-0 focus:border-neon-pink/50
              resize-none overflow-hidden placeholder:text-xs transition-colors
              ${isDisabled ? 'bg-white/[.03] opacity-60 cursor-not-allowed' : 'bg-white/[.05]'}
            `}
            style={{
              minHeight: '40px',
              maxHeight: '120px',
            }}
            onInput={(e) => {
              // Auto-resize textarea
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
            }}
          />
          
          {/* Character counter */}
          {content.length > 800 && (
            <div className={`
              absolute bottom-1 right-2 font-mono text-[11px]
              ${content.length > 1000 ? 'text-neon-pink-soft' : 'text-fg-meta'}
            `}>
              {content.length}/1000
            </div>
          )}
        </div>
        
        <button
          type="submit"
          disabled={isDisabled || !content.trim()}
          className={`
            p-2 rounded-[11px] transition-all
            flex items-center justify-center min-w-[40px] min-h-[40px]
            ${
              isDisabled || !content.trim()
                ? 'bg-white/[.06] text-fg-faint cursor-not-allowed'
                : 'bg-neon-gradient text-ink shadow-neon-pink-strong hover:opacity-90 focus:outline-none'
            }
          `}
          title={isSending ? 'Verzenden...' : isRateLimited ? `Wacht ${rateLimitTimeLeft}s` : isMessagingRestricted ? 'Beperkt' : 'Bericht verzenden'}
        >
          {isSending ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : isRateLimited ? (
            <span className="text-xs font-medium">{rateLimitTimeLeft}s</span>
          ) : isMessagingRestricted ? (
            <Lock className="h-4 w-4" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </form>
      
      {/* Upgrade flow for GIF/photo access (chat enhancements) */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        upgradeType="CHAT_ENHANCEMENTS"
        onUpgradeComplete={() => updateUser({ chatEnhancements: true })}
      />

      {/* Connection warning */}
      {!isConnected && (
        <div className="mt-2 text-sm bg-neon-pink/[.08] border border-neon-pink/30 rounded-field px-3 py-2">
          <span className="text-fg-bright font-medium">Niet verbonden met chatserver.</span>
          <span className="text-fg-muted ml-1">Berichten kunnen niet worden verzonden.</span>
        </div>
      )}

      {/* Rate limit warning - prominent spam warning */}
      {isRateLimited && (
        <div className="mt-2 text-sm bg-neon-pink/[.10] border border-neon-pink/40 rounded-field px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-neon-pink-soft" />
              <span className="font-medium text-fg-bright">
                Je verstuurt berichten te snel! Doe het wat rustiger aan en probeer het later opnieuw.
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-mono text-[11px] uppercase tracking-[.16em] text-fg-meta">Wacht</span>
              <span className="font-mono font-bold text-lg text-neon-pink-soft">{rateLimitTimeLeft}s</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectMessageInput;